import React, { useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";

const EditSessionModal = ({ isOpen, toggle, onSubmit, sessionData }) => {
  const validationSchema = Yup.object({
    name: Yup.string().required("Session name is required"),
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("startDate"), "End date cannot be before start date"),
  });

  // Convert date format string (dd-mm-yyyy or yyyy-mm-dd) → Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (typeof dateStr === "string" && dateStr.includes("-")) {
      const parts = dateStr.split("-");
      if (parts[0].length === 4) {
        // yyyy-mm-dd
        return new Date(dateStr);
      } else {
        // dd-mm-yyyy
        const [day, month, year] = parts;
        return new Date(`${year}-${month}-${day}`);
      }
    }
    return new Date(dateStr);
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const initialValues = {
    name: sessionData?.name || "",
    startDate: parseDate(sessionData?.startDate) || "",
    endDate: parseDate(sessionData?.endDate) || "",
  };

  const handleSubmit = (values) => {
    const payload = {
      id: sessionData?.id, // include ID for editing
      name: values.name,
      startDate: formatDate(values.startDate),
      endDate: formatDate(values.endDate),
    };

    if (onSubmit) onSubmit(payload);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Edit Session</ModalHeader>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, touched, errors }) => (
          <Form>
            <ModalBody>
              <FormGroup>
                <Label>
                  Session Name <span className="text-danger">*</span>
                </Label>
                <Field
                  name="name"
                  as={Input}
                  invalid={touched.name && !!errors.name}
                  placeholder="Enter session name (e.g., 2024-2025)"
                />
                <ErrorMessage
                  name="name"
                  component={FormFeedback}
                  className="d-block"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Start Date <span className="text-danger">*</span>
                </Label>
                <Flatpickr
                  className={`form-control ${
                    touched.startDate && errors.startDate ? "is-invalid" : ""
                  }`}
                  options={{ dateFormat: "d/m/Y" }}
                  value={values.startDate}
                  onChange={(date) => setFieldValue("startDate", date[0])}
                />
                <ErrorMessage
                  name="startDate"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  End Date <span className="text-danger">*</span>
                </Label>
                <Flatpickr
                  className={`form-control ${
                    touched.endDate && errors.endDate ? "is-invalid" : ""
                  }`}
                  options={{ dateFormat: "d/m/Y" }}
                  value={values.endDate}
                  onChange={(date) => setFieldValue("endDate", date[0])}
                />
                <ErrorMessage
                  name="endDate"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </FormGroup>
            </ModalBody>

            <ModalFooter>
              <Button className="btn-sm" color="secondary" onClick={toggle}>
                Cancel
              </Button>
              <Button className="btn-sm" color="success" type="submit">
                Save changes
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditSessionModal;
